import { ArrowDropDown } from '@mui/icons-material';
import { Box, Button, ButtonProps, Checkbox, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Popover, Stack, debounce } from '@mui/material';
import { t } from 'i18next';
import { ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { ComponentLoader } from '../../../../../common/components/ComponentLoader';
import { useMounted } from '../../../../../common/hooks/useMounted';
import { Preset } from '../../../../../common/models/valueObjects/Preset';
import { SearchInput } from './SearchInput';

type Props = {
  value: Preset[];
  buttonLabels?: {
    emptyState?: string;
    nonEmptyPrefix?: string;
  };
  prefixIcon?: ReactNode;
  defaultItems?: Preset[];
  numberOfItemsToShow?: number;
  onChange: (value: Preset[]) => void;
  search?: {
    onSearch: (keyword: string, exclude: string[], limit: number) => Promise<Preset[]>;
    placeholder?: string;
    runOnMount?: boolean;
  };
  hideReset?: boolean;
  allowSelectAll?: boolean;
  disabled?: boolean;
};

const DEFAULT_DISPLAYED_ITEMS = 10;
const MIN_SEARCH_ITEMS = 3;

export const DropdownList = ({
  value,
  buttonLabels = {},
  defaultItems = [],
  prefixIcon,
  numberOfItemsToShow = DEFAULT_DISPLAYED_ITEMS,
  onChange,
  search,
  hideReset = false,
  allowSelectAll = false,
  disabled = false,
}: Props) => {
  const id = useId();
  const [displayedItems, setDisplayedItems] = useState(defaultItems);
  const [loading, setLoading] = useState(false);
  const [selectedItems, updateSelectedItems] = useState<Preset[]>(value);
  const inputChangedRef = useRef<boolean>(false);
  const inputValueRef = useRef<string>('');

  const setSelectedItems = useCallback(
    (items: Preset[]) => {
      updateSelectedItems(items);
      inputChangedRef.current = true;
    },
    [inputChangedRef, updateSelectedItems]
  );

  // We leverage double managed state because we have an apply step which will trigger outer change only if we close
  // the popper. Until then, this is just an internal state where we can find and mark required elements.
  useEffect(() => {
    if (value === selectedItems) {
      return;
    }

    updateSelectedItems(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // we depend only on value changes, so don't trigger this if internal state change happens

  const mounted = useMounted();

  const [popperOpen, setPopperOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement | null>(null);

  const { emptyState: emptyButtonLabel = t('advancedFilters.selectElements'), nonEmptyPrefix: buttonPrefix = t('advancedFilters.items') } = buttonLabels;

  const mainButtonVariant = popperOpen ? 'contained' : 'outlined';
  const hasAnyValue = selectedItems.length > 0;
  const buttonLabel = hasAnyValue ? `${buttonPrefix} (${selectedItems.length})` : emptyButtonLabel;
  const mainButtonProps: ButtonProps = {};
  if (prefixIcon) {
    mainButtonProps['startIcon'] = prefixIcon;
  }

  const handleReset = () => {
    setSelectedItems([]);

    handleClose([]);
  };

  const handleClose = (forcedData: Preset[] | null = null) => {
    // Clear search items
    setDisplayedItems(defaultItems);
    setPopperOpen(false);

    // If there was no input change, no need to call external update
    if (!inputChangedRef.current) {
      return;
    }
    inputChangedRef.current = false;

    // if we want to force output data, then we can pass it in.
    // eg, when handling reset, the selected items are still not propagated into state.
    const outputData = forcedData ?? selectedItems;
    onChange(outputData);
  };

  const handleToggle = (item: Preset) => {
    const itemId = item.getId();
    const hadSelectedItem = selectedItems.some((i) => i.getId() === itemId);

    let newSelectedItems;
    if (!hadSelectedItem) {
      newSelectedItems = [...selectedItems, item];
    } else {
      newSelectedItems = [...selectedItems].filter((i) => i.getId() !== itemId);
    }

    setSelectedItems(newSelectedItems);
  };

  const handleToggleAll = () => {
    if (selectedItems.length === defaultItems.length) {
      setSelectedItems([]);
      return;
    }

    setSelectedItems([...defaultItems]);
  };

  const hasSearch = search !== undefined;
  const { placeholder: searchPlaceholder = t('advancedFilters.search'), onSearch = async () => defaultItems, runOnMount = false } = search || {};

  const doSearch = (query: string) => {
    inputValueRef.current = query;
    if (query === '' && !runOnMount) {
      setDisplayedItems(defaultItems);
      return;
    }
    setLoading(true);

    // Exclude currently selected items from search results
    const exclude = value.map((v) => v.getLabel());

    // Limit the amount of results fetched:
    // - if the user is currently searching, do not limit the search results
    // - otherwise, fetch the amount of results that will "fill" the list
    //   - if the list is already complete, only fetch MIN_SEARCH_ITEMS more results
    const limit = query === '' ? Math.max(DEFAULT_DISPLAYED_ITEMS - exclude.length, MIN_SEARCH_ITEMS) : DEFAULT_DISPLAYED_ITEMS;

    onSearch(query, exclude, limit)
      .then((items: Preset[]) => {
        if (!mounted.current) {
          return;
        }
        setDisplayedItems(items);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted.current) {
          return;
        }
        setLoading(false);
        // if search fails, return defaultItems

        setDisplayedItems(defaultItems);
      });
  };

  const handleSearch = debounce(doSearch, 200);

  // Slice to display just the max number of items
  const itemsToDisplay = displayedItems.slice(0, numberOfItemsToShow);
  const selectedItemsToDisplay = hasSearch && value.length > 0 && inputValueRef.current === '' ? value : [];

  const canCheckAll = allowSelectAll && !hasSearch && defaultItems.length > 0 && itemsToDisplay.length > 0;
  const isAllChecked = canCheckAll && selectedItems.length === defaultItems.length;
  const isPartiallyChecked = canCheckAll && selectedItems.length > 0;

  return (
    <>
      <Button
        id={id}
        ref={anchorEl}
        endIcon={<ArrowDropDown />}
        {...mainButtonProps}
        disabled={disabled}
        variant={mainButtonVariant}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();

          setPopperOpen(true);
        }}
      >
        {buttonLabel}
      </Button>

      <Popover
        open={popperOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        role="dialog"
        aria-labelledby={id}
        aria-busy={loading}
        anchorEl={anchorEl.current}
        onTransitionEnter={() => doSearch('')}
        onClose={() => handleClose()}
      >
        <ComponentLoader loading={loading} backgroundColor="transparent">
          <Paper sx={{ pt: hasSearch ? 0 : 1, pb: 0.875, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 2rem)' }}>
            {hasSearch && (
              <Box sx={{ p: 1 }}>
                <SearchInput onChange={handleSearch} label={searchPlaceholder} loading={loading} />
              </Box>
            )}
            <List disablePadding dense sx={{ overflow: 'auto', width: hasSearch ? 360 : 250, li: { py: 0 }, label: { cursor: 'pointer' } }}>
              {!loading && itemsToDisplay.length === 0 && selectedItemsToDisplay.length === 0 && (
                <ListItem key="blankState">
                  <ListItemText primary={t('advancedFilters.noResultsForCurrentSearch')} />
                </ListItem>
              )}
              {canCheckAll && (
                <ListItemButton
                  component="li"
                  tabIndex={-1}
                  role={undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggleAll();
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 'initial' }}>
                    <Checkbox id="select-all" edge="start" checked={isAllChecked} indeterminate={isPartiallyChecked && !isAllChecked} disableRipple={true} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ component: 'label', htmlFor: 'select-all' }} primary={t('advancedFilters.all')} />
                </ListItemButton>
              )}
              {[...selectedItemsToDisplay, ...itemsToDisplay].map((item) => {
                const labelId = `checkbox-list-label-${item.getId()}`;
                const isChecked = selectedItems.some((i) => i.getId() === item.getId());
                return (
                  <ListItemButton
                    key={item.getId()}
                    component="li"
                    tabIndex={-1}
                    role={undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      handleToggle(item);
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 'initial' }}>
                      <Checkbox id={labelId} edge="start" checked={isChecked} disableRipple={true} />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ component: 'label', htmlFor: labelId }}
                      primary={item.getLabel()}
                      secondary={item.getCategory()}
                      sx={{ wordBreak: 'break-word' }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
            <Stack sx={{ justifyContent: 'space-between', px: 0.75, mt: 0.5 }} direction="row-reverse">
              <Button disabled={loading} onClick={() => handleClose()}>
                {t('apply')}
              </Button>
              {!hideReset && (
                <Button disabled={loading} onClick={() => handleReset()} color="light-grey">
                  {t('reset')}
                </Button>
              )}
            </Stack>
          </Paper>
        </ComponentLoader>
      </Popover>
    </>
  );
};
